import type { GaiaJsonValue, ReviewExtractionInDb } from '@/api';
import { reviewRiskLevel } from '@/components/design/statusMaps';
import type { GaiaRiskLevel } from '@/components/design/base/GaiaRiskPill.vue';
import type { ExtractedField, FieldType } from '@/types/evaluation';
import { isValueOverwritten } from '@/utils/overwrite';

/**
 * One builder for the `ExtractedField` view-model, replacing the three copies
 * that drifted apart in `ReviewDetailView`, `CellDetailsModal` and the board.
 *
 * The shape it produces is what `GaiaExtractionDetail` and
 * `GaiaExtractionFieldRow` read: the AI's own answer under `ai_*`, the human
 * overwrite under `overwritten_*`, and a `has_*_overwrite` flag per part so the
 * UI can label a value "Response" vs "AI Response" without comparing values.
 */

/** An extraction plus the uid the accordion keys its rows on. */
export interface ReviewExtractedField extends ExtractedField {
  uid: string;
}

/** One group of fields sharing an evaluation answer, as the field list draws it. */
export interface ReviewFieldGroup {
  /** The raw evaluation answer — also the group's collapse key. */
  key: string;
  label: string;
  level: GaiaRiskLevel;
  fields: ReviewExtractedField[];
}

/** Groups the product ranks; anything else sorts alphabetically after them. */
const GROUP_ORDER = ['High', 'Medium', 'Human Review', 'Low'];

/** Shown for a field whose evaluation produced no answer at all. */
const UNGROUPED = 'Other';

/** Read `{answer, reason}` off an evaluation that may be a bare string. */
function readEvaluation(evaluation: GaiaJsonValue): ExtractedField['ai_evaluation'] {
  if (evaluation === null || evaluation === undefined) return null;
  if (typeof evaluation === 'string') return { answer: evaluation };

  const record = evaluation as Record<string, unknown>;
  return {
    // eslint-disable-next-line @typescript-eslint/prefer-nullish-coalescing -- empty string must normalise to undefined
    answer: (record.answer as string | string[] | undefined) || undefined,
    // eslint-disable-next-line @typescript-eslint/prefer-nullish-coalescing -- empty string must normalise to undefined
    reason: (record.reason as string | undefined) || undefined,
  };
}

/** The review detail's field list: one entry per extraction, sorted by name. */
export function buildExtractedField(extraction: ReviewExtractionInDb): ReviewExtractedField {
  const overwrite = extraction.human_overwrite;
  const hasOverwrite = overwrite !== null;
  const aiEvaluation = readEvaluation(extraction.evaluation);

  return {
    uid: extraction.uid,
    field: extraction.static_prompt_copy.name || 'Unknown Field',
    // Always the original AI values — the overwrite travels separately so the
    // UI can show what the human changed it from.
    ai_value: extraction.value,
    ai_reason: extraction.reason,
    ai_page: extraction.page,
    extraction_prompt: extraction.static_prompt_copy.prompt || undefined,
    example_data: extraction.static_prompt_copy.example_data ?? [],
    processing_state: extraction.processing_state,
    ai_evaluation: aiEvaluation,
    ai_highlighted_text: extraction.highlighted_text,
    // Each part is overwritten only where the stored overwrite actually differs
    // from what the AI produced. The generated overwrite type makes every part
    // required-and-null rather than optional, so the older `!== undefined` test
    // this replaces was really `hasOverwrite` — one edited value marked the
    // reason, page, highlights and evaluation as human-authored too.
    has_value_overwrite: hasOverwrite && isValueOverwritten(overwrite.value, extraction.value),
    has_reason_overwrite: hasOverwrite && isValueOverwritten(overwrite.reason, extraction.reason),
    has_page_overwrite: hasOverwrite && isValueOverwritten(overwrite.page, extraction.page),
    has_highlighted_text_overwrite:
      hasOverwrite && isValueOverwritten(overwrite.highlighted_text, extraction.highlighted_text),
    has_evaluation_overwrite:
      hasOverwrite && isValueOverwritten(overwrite.evaluation, aiEvaluation?.answer),
    has_evaluation_reason_overwrite:
      hasOverwrite && isValueOverwritten(overwrite.evaluation_reason, aiEvaluation?.reason),
    overwritten_value: hasOverwrite ? overwrite.value : undefined,
    overwritten_reason: hasOverwrite ? (overwrite.reason ?? null) : undefined,
    overwritten_page: hasOverwrite ? (overwrite.page ?? null) : undefined,
    overwritten_highlighted_text: hasOverwrite ? (overwrite.highlighted_text ?? null) : undefined,
    overwritten_evaluation:
      hasOverwrite && typeof overwrite.evaluation === 'string' ? overwrite.evaluation : undefined,
    overwritten_evaluation_reason: hasOverwrite ? (overwrite.evaluation_reason ?? null) : undefined,
    overwrite_uid: hasOverwrite ? overwrite.uid || null : null,
    approved: extraction.approved,
    field_type: extraction.static_prompt_copy.response_type,
    enum_options:
      typeof extraction.static_prompt_copy.response_type === 'object'
        ? extraction.static_prompt_copy.response_type
        : undefined,
  };
}

/** One cell of the insight grid, as the table view hands it over. */
export interface ReviewCellData {
  uid: string;
  value: GaiaJsonValue;
  reason: string | null;
  page: number | null;
  processing_state: string;
  highlighted_text: string[] | null;
  prompt_name?: string;
  file_review_uid?: string;
  field_type?: string | object;
  evaluation?: { answer?: string; reason?: string } | null;
  approved?: boolean;
  human_overwrite?: {
    uid?: string;
    value?: GaiaJsonValue;
    reason?: string | null;
    page?: number | null;
    highlighted_text?: string[] | null;
    evaluation?: GaiaJsonValue;
    evaluation_reason?: string | null;
  } | null;
}

/**
 * The same view-model built from an insight-grid cell. A cell carries no prompt
 * copy, so `extraction_prompt` / `example_data` are absent and the response type
 * has to be passed in by the caller, which is the only place that still holds
 * the matching extraction.
 */
export function buildExtractedFieldFromCell(
  cell: ReviewCellData,
  options: { fieldType?: FieldType; enumOptions?: ExtractedField['enum_options'] } = {}
): ReviewExtractedField {
  const overwrite = cell.human_overwrite ?? {};

  const hasValueOverwrite = overwrite.value !== undefined;
  const hasReasonOverwrite = overwrite.reason !== undefined;
  const hasPageOverwrite = overwrite.page !== undefined;
  const hasHighlightedTextOverwrite = overwrite.highlighted_text !== undefined;
  const hasEvaluationOverwrite = overwrite.evaluation !== undefined;
  const hasEvaluationReasonOverwrite = overwrite.evaluation_reason !== undefined;

  return {
    uid: cell.uid,
    // eslint-disable-next-line @typescript-eslint/prefer-nullish-coalescing -- empty string must use the fallback
    field: cell.prompt_name || 'Unknown Field',
    field_type: options.fieldType ?? 'str',
    ai_value: cell.value,
    ai_reason: cell.reason,
    ai_page: cell.page,
    ai_highlighted_text: cell.highlighted_text,
    ai_evaluation: cell.evaluation ?? null,
    processing_state: null,
    has_value_overwrite: hasValueOverwrite,
    has_reason_overwrite: hasReasonOverwrite,
    has_page_overwrite: hasPageOverwrite,
    has_highlighted_text_overwrite: hasHighlightedTextOverwrite,
    has_evaluation_overwrite: hasEvaluationOverwrite,
    has_evaluation_reason_overwrite: hasEvaluationReasonOverwrite,
    overwritten_value: hasValueOverwrite ? overwrite.value : null,
    overwritten_reason: hasReasonOverwrite ? (overwrite.reason ?? null) : null,
    overwritten_page: hasPageOverwrite ? (overwrite.page ?? null) : null,
    overwritten_highlighted_text: hasHighlightedTextOverwrite
      ? (overwrite.highlighted_text ?? null)
      : null,
    overwritten_evaluation: hasEvaluationOverwrite
      ? (overwrite.evaluation as string | string[] | null)
      : null,
    overwritten_evaluation_reason: hasEvaluationReasonOverwrite
      ? (overwrite.evaluation_reason ?? null)
      : null,
    overwrite_uid: overwrite.uid ?? null,
    approved: cell.approved ?? false,
    enum_options: options.enumOptions,
  };
}

/** The effective evaluation answer — a human overwrite wins over the AI's. */
function effectiveAnswer(field: ExtractedField): string | string[] | undefined {
  if (field.has_evaluation_overwrite && field.overwritten_evaluation) {
    return field.overwritten_evaluation;
  }
  return field.ai_evaluation?.answer ?? undefined;
}

/** The single answer a risk pill colours from — the first, when there are several. */
export function evaluationAnswerForRisk(field: ExtractedField): string | undefined {
  const answer = effectiveAnswer(field);
  if (Array.isArray(answer)) return answer[0];
  return typeof answer === 'string' ? answer : undefined;
}

/** The answer as shown to a reader — every value, or `N/A` when there is none. */
export function evaluationAnswerText(field: ExtractedField): string {
  const answer = effectiveAnswer(field);
  if (Array.isArray(answer)) return answer.join(', ');
  return typeof answer === 'string' ? answer : 'N/A';
}

/**
 * Group fields by evaluation answer, ranked High → Medium → Human Review → Low,
 * with org-specific answers sorted alphabetically after them. The answer doubles
 * as the group's key, so a group's label is the answer itself.
 */
export function groupFieldsByRisk(fields: ReviewExtractedField[]): ReviewFieldGroup[] {
  const groups = new Map<string, ReviewExtractedField[]>();

  for (const field of fields) {
    // eslint-disable-next-line @typescript-eslint/prefer-nullish-coalescing -- empty string must fall through to the next operand
    const key = evaluationAnswerForRisk(field) || UNGROUPED;
    const existing = groups.get(key);
    if (existing) existing.push(field);
    else groups.set(key, [field]);
  }

  return [...groups.entries()]
    .sort(([a], [b]) => {
      const indexA = GROUP_ORDER.indexOf(a);
      const indexB = GROUP_ORDER.indexOf(b);
      if (indexA === -1 && indexB === -1) return a.localeCompare(b);
      if (indexA === -1) return 1;
      if (indexB === -1) return -1;
      return indexA - indexB;
    })
    .map(([key, groupFields]) => ({
      key,
      label: key,
      level: reviewRiskLevel(key),
      fields: groupFields,
    }));
}
